import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractLegacyUpgradeService } from '../../abstracts/services/onboarding/legacy-upgrade.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { ILegacyUpgrade } from '../../interfaces/onboarding/legacy-upgrade/legacy-upgrade.interface';
import { PageState } from '../../models/page-state/page-state.model';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import {
  HydrationRoles,
  HydrationSources,
} from '../../store/common/interfaces/hydration-status-store.interface';
import { DashboardStateIdAction } from '../../store/onboarding/actions/enrollment.actions';
import { selectDashboardStateId } from '../../store/onboarding/reducers+selectors/enrollment.selector';
import { destructSubscriptionArray } from '../../ui-helpers/functions/destruct-subscription-array';
import { applyEnrollmentIdToQueryParams } from '../../ui-helpers/functions/user-context-url.func';
import { HydrationService } from '../login/hydration.service';
import { EnrollmentExtendedService } from './enrollment-extended.service';
import { PageStateService } from './page-state.service';

@Injectable({
  providedIn: 'root',
})
export class LegacyUpgradeService
  extends AbstractLegacyUpgradeService
  implements OnDestroy
{
  private apiUrl = environment.apiUrl;
  private legacyUpgradeControllerSuffix =
    'Onboarding/Onboarding/LegacyUpgrade/';

  private subscriptions: Subscription[] = [];

  constructor(
    private httpClient: HttpClient,
    private hs: HydrationService,
    private ps: PageStateService,
    private ees: EnrollmentExtendedService,
    private store: Store<IAppState>,
  ) {
    super();
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async redirectToLegacyUpgrade(): Promise<boolean> {
    await this.hs.HydrateOnboardingStore(
      HydrationRoles.onboardingProspectOrOnboardingClient,
      HydrationSources.legacyUpgrade,
    );
    const dashboardStateIdObj: { dashboardStateId: string } =
      await firstValueFrom(this.store.select(selectDashboardStateId));

    if (
      dashboardStateIdObj &&
      dashboardStateIdObj.dashboardStateId !== null &&
      dashboardStateIdObj.dashboardStateId !== '-1'
    ) {
      const result: PageState = await this.ps.getPageStateById(
        dashboardStateIdObj.dashboardStateId,
      );
      this.store.dispatch(
        DashboardStateIdAction({ dashboardStateId: result.id }),
      );
      return false;
    }
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.legacyUpgradeControllerSuffix}legacy-upgrade-navigator`;
    const res = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }),
    );
    const legacyPageState = new PageState(res.data);
    this.store.dispatch(
      DashboardStateIdAction({ dashboardStateId: legacyPageState.id }),
    );
    return true;
  }

  async saveLegacyUpgrade(model: ILegacyUpgrade): Promise<IApiResponse> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.legacyUpgradeControllerSuffix}save-legacy-upgrade`;
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, model, { params }),
    );
    return result;
  }
}
